import type { CSSProperties } from 'react';

import type { RegionComponent, RegionDetail } from '../../../../api/regions';
import { SectionCard } from '../../regional/BucketStates';

import { RegionAreaChips } from './RegionAreaChips';
import { regionLevelLabel } from './regionLevelLabel';
import { RegionReadRow } from './RegionReadRow';
import { useSetRegionDirector } from './regionsQueries';
import { SubregionDirector } from './SubregionDirector';
import { directorOptions, useRegionalDirectors } from './useRegionalDirectors';

interface Props {
  detail: RegionDetail;
  components: RegionComponent[];
  color: string;
}

const EM_DASH = '—';

const fieldStyle: CSSProperties = {
  height: 34,
  padding: '0 11px',
  borderRadius: 9,
  border: '1px solid var(--nb-border)',
  background: 'var(--nb-bg)',
  font: 'inherit',
  fontSize: 13,
  color: 'var(--nb-ink)',
  boxSizing: 'border-box',
  width: '100%',
};

/** Region profile: read-grid + composed-of geographies + assignable director. */
export function RegionProfilePanel({ detail, components, color }: Props) {
  const setDirector = useSetRegionDirector();
  const rdQ = useRegionalDirectors();
  const options = directorOptions(rdQ.data ?? [], detail.directorUserId, detail.directorName);

  return (
    <SectionCard title="Region profile">
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <RegionReadRow label="Region code" value={detail.code || EM_DASH} />
        <RegionReadRow label="Level" value={regionLevelLabel(detail)} />
        <RegionReadRow label="Venues" value={detail.venueCount} />
        <RegionReadRow label="Subregions" value={detail.subregionCount} />
      </div>

      <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--nb-border)' }}>
        <div className="nb-eyebrow" style={{ fontSize: 9, marginBottom: 7 }}>
          Composed of ({components.length})
        </div>
        <RegionAreaChips
          areas={components}
          color={color}
          emptyText="No areas — composed from subregions."
        />
      </div>

      <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--nb-border)' }}>
        <div className="nb-eyebrow" style={{ fontSize: 9, marginBottom: 7 }}>
          Regional director
        </div>
        {detail.parentId === null ? (
          <select
            aria-label="Regional director"
            value={detail.directorUserId ?? ''}
            disabled={setDirector.isPending || rdQ.isLoading}
            onChange={(e) => setDirector.mutate({ id: detail.id, userId: e.target.value || null })}
            style={fieldStyle}
          >
            <option value="">— Unassigned —</option>
            {options.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        ) : (
          <SubregionDirector
            detail={detail}
            onClear={() => setDirector.mutate({ id: detail.id, userId: null })}
            clearing={setDirector.isPending}
          />
        )}
      </div>
    </SectionCard>
  );
}
